import {
  DARMOSHARK_54L_M3_ULTRA_PRODUCT_ID,
  DARMOSHARK_M2_SE_PRODUCT_ID,
  DARMOSHARK_M3_MICRO_V2_PRODUCT_ID,
  DARMOSHARK_M5_V2_PRODUCT_ID,
  DARMOSHARK_M6_ULTRA_PRODUCT_ID,
  DARMOSHARK_M9_PRODUCT_ID,
  DARMOSHARK_VENDOR_ID,
  MS502_V3_PRODUCT_ID,
  WANI_G1_PRODUCT_ID,
  WANI_H2_PRODUCT_ID,
  vidPidKey,
} from './DeviceCatalog';

function normalizeVersion(version) {
  const text = String(version || '').trim();
  if (!text) return '';
  return text.replace(/^v/i, '');
}

function adapterKey(vendorId, productId, version) {
  return `${vidPidKey(vendorId, productId)}:${normalizeVersion(version)}`;
}

const registry = new Map();

export function registerAdapter(definition) {
  const key = adapterKey(
    definition?.vendorId,
    definition?.productId,
    definition?.protocolVersion
  );
  if (
    !definition ||
    !Number.isInteger(definition.vendorId) ||
    !Number.isInteger(definition.productId) ||
    !normalizeVersion(definition.protocolVersion)
  ) {
    throw new TypeError('Adapter 必须声明有效的 VID、PID 和精确协议版本');
  }
  if (registry.has(key)) {
    throw new Error(`Adapter 重复注册: ${key}`);
  }
  registry.set(
    key,
    Object.freeze({
      ...definition,
      protocolVersion: normalizeVersion(definition.protocolVersion),
    })
  );
}

export function resolveAdapter(vendorId, productId, protocolVersion) {
  return (
    registry.get(adapterKey(vendorId, productId, protocolVersion)) || null
  );
}

export function listRegisteredAdapters() {
  return [...registry.values()];
}

const sharedMouseCapabilities = Object.freeze({
  dpi: true,
  pollingRate: true,
  buttonMapping: true,
  macros: true,
  wheelSettings: false,
  hitsSettings: false,
  lightingSettings: false,
  firmwareUpgrade: true,
});

// 带灯的型号：在共享能力上打开灯光设置页。
const lightingMouseCapabilities = Object.freeze({
  ...sharedMouseCapabilities,
  lightingSettings: true,
});

// LM20 平台的 4.0 机型：在带灯能力上再开滚轮与按键性能（HITS）配置页。
const lm20MouseCapabilities = Object.freeze({
  ...lightingMouseCapabilities,
  wheelSettings: true,
  hitsSettings: true,
});

// 当前仓库已经存在并测试过的精确协议版本。后续版本必须显式登记。
[
  {
    modelId: 'darmoshark-m9',
    productId: DARMOSHARK_M9_PRODUCT_ID,
    capabilities: sharedMouseCapabilities,
  },
  {
    modelId: 'darmoshark-m6-ultra',
    productId: DARMOSHARK_M6_ULTRA_PRODUCT_ID,
    capabilities: sharedMouseCapabilities,
  },
  {
    modelId: 'darmoshark-m2-se',
    productId: DARMOSHARK_M2_SE_PRODUCT_ID,
    capabilities: lightingMouseCapabilities,
  },
  {
    modelId: 'darmoshark-54l-m3-ultra',
    productId: DARMOSHARK_54L_M3_ULTRA_PRODUCT_ID,
    capabilities: lightingMouseCapabilities,
  },
  {
    modelId: 'darmoshark-m5-v2',
    productId: DARMOSHARK_M5_V2_PRODUCT_ID,
    capabilities: lightingMouseCapabilities,
  },
  {
    modelId: 'darmoshark-m3-micro-v2',
    productId: DARMOSHARK_M3_MICRO_V2_PRODUCT_ID,
    capabilities: lightingMouseCapabilities,
  },
].forEach(({ modelId, productId, capabilities }) => {
  ['1.06', '3.0', '4.0'].forEach((protocolVersion) => {
    registerAdapter({
      id: `${modelId}-v${protocolVersion}`,
      modelId,
      vendorId: DARMOSHARK_VENDOR_ID,
      productId,
      protocolVersion,
      capabilities,
    });
  });
});

// MS805（0x9001）出厂即 4.0，其它版本不登记：读到别的版本号要明确报「尚未适配」。
registerAdapter({
  id: 'ms502-v3-v4.0',
  modelId: 'ms502-v3',
  vendorId: DARMOSHARK_VENDOR_ID,
  productId: MS502_V3_PRODUCT_ID,
  protocolVersion: '4.0',
  capabilities: lm20MouseCapabilities,
});

// wani G1 / H2 出厂即 4.0，经 8K Receiver 或有线/蓝牙直连。
// 能力与 MS805 相同；其它协议版本不登记。
[
  { modelId: 'wani-g1', productId: WANI_G1_PRODUCT_ID },
  { modelId: 'wani-h2', productId: WANI_H2_PRODUCT_ID },
].forEach(({ modelId, productId }) => {
  registerAdapter({
    id: `${modelId}-v4.0`,
    modelId,
    vendorId: DARMOSHARK_VENDOR_ID,
    productId,
    protocolVersion: '4.0',
    capabilities: lm20MouseCapabilities,
  });
});

export { normalizeVersion as normalizeProtocolVersion };
